import styled from "styled-components";
import React from "react";
import { ArrowRight, CheckCircle2, Loader2, RotateCcw } from "lucide-react";
import { router } from "@inertiajs/react";

import { useLanguage } from "../../../../contexts/language";
import { PageHeader } from "../../../../components/page-header";
import { ExitSessionButton } from "../../../../components/exit-session-button";

const getJson = (url) =>
  fetch(url, {
    headers: { Accept: "application/json" },
    credentials: "same-origin",
  }).then((res) => res.json().then((json) => ({ ok: res.ok, json })));

const postJson = (url) =>
  fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    credentials: "same-origin",
    body: "{}",
  }).then((res) => res.json().then((json) => ({ ok: res.ok, json })));

const rupiah = (value) => `Rp ${Number(value || 0).toLocaleString("id-ID")}`;

const formatDate = (iso, language) => {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString(
    language === "id" ? "id-ID" : "en-GB",
    { day: "numeric", month: "short", year: "numeric" },
  );
};

const BookingDetail = (props) => {
  const { t, language } = useLanguage();
  const buildingCode = props.building_code;
  const copy = t.bookingDetail;

  const [detail, setDetail] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [claiming, setClaiming] = React.useState(false);

  const load = React.useCallback(() => {
    getJson("/api/v1/guest_portal/booking_detail")
      .then((res) => {
        if (res.ok && res.json.data) setDetail(res.json.data);
        else setError(copy.loadError);
      })
      .finally(() => setLoading(false));
  }, [copy.loadError]);

  React.useEffect(() => {
    load();
  }, [load]);

  const claimRefund = () => {
    setClaiming(true);
    setError("");
    postJson("/api/v1/guest_portal/deposit_refund").then((res) => {
      if (res.ok && res.json.data && res.json.data.verify_path) {
        window.location.href = res.json.data.verify_path;
      } else {
        setError((res.json && res.json.message) || copy.claimError);
        setClaiming(false);
        load();
      }
    });
  };

  const goToDeposit = () =>
    router.visit(`/hebe/${buildingCode}/hgs/self-deposit`);

  // The self-deposit page lands on its "done" (check-in instructions + booking QR) step once the
  // deposit is paid, so it doubles as the always-available check-in instructions page.
  const goToInstructions = () =>
    router.visit(`/hebe/${buildingCode}/hgs/self-deposit`);

  return (
    <Page>
      <PageHeader
        title={copy.title}
        backPath={`/hebe/${buildingCode}/hgs/bookings`}
        buildingCode={buildingCode}
        showGuestInfo
      />
      <Inner>
        {error ? <ErrorBox>{error}</ErrorBox> : null}

        {loading ? (
          <Centered>
            <Loader2 size={22} className="spin" />
          </Centered>
        ) : null}

        {!loading && detail ? (
          <>
            <SummaryCard>
              <GuestName>{detail.guest_name}</GuestName>
              <Meta>
                {detail.nights} {copy.nights} · {detail.rooms_count}{" "}
                {copy.rooms}
              </Meta>
              <Dates>
                <span>
                  {copy.checkIn}: {formatDate(detail.check_in, language)}
                </span>
                <span>
                  {copy.checkOut}: {formatDate(detail.check_out, language)}
                </span>
              </Dates>
            </SummaryCard>

            <SectionHeading>{copy.lineItems}</SectionHeading>
            {detail.lines.map((line) => (
              <LineCard
                key={line.id}
                line={line}
                copy={copy}
                language={language}
              />
            ))}

            {!detail.deposit_paid ? (
              <DepositCard>
                <div>
                  <CardTitle>{copy.depositSection}</CardTitle>
                  <Muted>{copy.depositPending}</Muted>
                </div>
                <Primary onClick={goToDeposit}>
                  {copy.goToPayment} <ArrowRight size={16} />
                </Primary>
              </DepositCard>
            ) : (
              <DepositCard>
                <div>
                  <CardTitle>{copy.checkInInstructionsTitle}</CardTitle>
                  <Muted>{copy.checkInInstructionsHint}</Muted>
                </div>
                <Primary onClick={goToInstructions}>
                  {copy.viewCheckInInstructions} <ArrowRight size={16} />
                </Primary>
              </DepositCard>
            )}

            <RefundSection
              refund={detail.refund}
              copy={copy}
              claiming={claiming}
              onClaim={claimRefund}
            />

            <ExitSessionButton buildingCode={buildingCode} />
          </>
        ) : null}
      </Inner>
    </Page>
  );
};

const LineCard = (props) => {
  const line = props.line;
  const copy = props.copy;
  const statusTone =
    line.status === "checked_out"
      ? "out"
      : line.status === "checked_in"
        ? "in"
        : "reserved";

  return (
    <Card>
      <CardTop>
        <RoomType>{line.room_type || copy.room}</RoomType>
        <Badge $tone={statusTone}>
          {copy.lineStatus[line.status] || line.status}
        </Badge>
      </CardTop>
      <Meta>
        {copy.roomNumber}: {line.room_number || copy.roomTbd}
      </Meta>
      <Dates>
        <span>
          {copy.checkIn}: {formatDate(line.check_in, props.language)}
        </span>
        <span>
          {copy.checkOut}: {formatDate(line.check_out, props.language)} ·{" "}
          {line.nights} {copy.nights}
        </span>
      </Dates>
      <RoomRefund>{copy.refundStateLabel[line.refund_state] || ""}</RoomRefund>
    </Card>
  );
};

const RefundSection = (props) => {
  const refund = props.refund || {};
  const copy = props.copy;
  const batches = refund.batches || [];

  return (
    <RefundCard>
      <SectionRow>
        <RotateCcw size={20} />
        <CardTitle>{copy.refundSection}</CardTitle>
      </SectionRow>

      {refund.can_claim ? (
        <>
          <RefundLine>
            {copy.refundableNow}: <b>{rupiah(refund.refundable_now_idr)}</b>
          </RefundLine>
          <Muted>
            {copy.feeNote}: {rupiah(refund.fee_idr)}
          </Muted>
          <Primary disabled={props.claiming} onClick={props.onClaim}>
            {props.claiming ? (
              <Loader2 size={16} className="spin" />
            ) : (
              copy.claimRefund
            )}
          </Primary>
        </>
      ) : refund.held_count > 0 ? (
        <Muted>{copy.refundWaitingHk}</Muted>
      ) : batches.length === 0 ? (
        <Muted>{copy.refundNotAvailable}</Muted>
      ) : null}

      {refund.held_count > 0 ? (
        <PendingNote>
          {copy.pendingReview}: <b>{rupiah(refund.pending_review_idr)}</b> (
          {refund.held_count} {copy.rooms})
        </PendingNote>
      ) : null}

      {batches.length > 0 ? (
        <Batches>
          <SubHeading>{copy.refundBatches}</SubHeading>
          {batches.map((batch) => (
            <BatchRow key={batch.id}>
              <div>
                <b>{rupiah(batch.amount_idr)}</b> · {batch.rooms} {copy.rooms}
                <BatchStatus $done={batch.completed}>
                  {batch.completed ? <CheckCircle2 size={13} /> : null}{" "}
                  {copy.batchStatus[batch.status] || batch.status}
                </BatchStatus>
              </div>
              {batch.verify_path ? (
                <ClaimLink href={batch.verify_path}>
                  {copy.openPayout} <ArrowRight size={14} />
                </ClaimLink>
              ) : null}
            </BatchRow>
          ))}
        </Batches>
      ) : null}
    </RefundCard>
  );
};

const Page = styled.div`
  background: #faf8f5;
  min-height: 100vh;
`;

const Inner = styled.div`
  margin: 0 auto;
  max-width: 768px;
  padding: 24px 16px 40px;
`;

const Centered = styled.div`
  display: flex;
  justify-content: center;
  padding: 24px 0;

  .spin {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const SummaryCard = styled.div`
  background: #fff;
  border: 1px solid #ececec;
  border-radius: 14px;
  margin-bottom: 18px;
  padding: 16px 18px;
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #ececec;
  border-radius: 14px;
  margin-bottom: 12px;
  padding: 16px 18px;
`;

const CardTop = styled.div`
  align-items: flex-start;
  display: flex;
  gap: 12px;
  justify-content: space-between;
`;

const GuestName = styled.div`
  color: #1a1a1a;
  font-size: 17px;
  font-weight: 700;
`;

const RoomType = styled.div`
  color: #1a1a1a;
  font-size: 16px;
  font-weight: 700;
`;

const Badge = styled.span`
  align-items: center;
  background: ${(p) =>
    p.$tone === "out" ? "#eef0f2" : p.$tone === "in" ? "#e7f6ec" : "#fbf2dd"};
  border-radius: 999px;
  color: ${(p) =>
    p.$tone === "out" ? "#566170" : p.$tone === "in" ? "#1f7a44" : "#8a6d1a"};
  display: inline-flex;
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 700;
  padding: 5px 10px;
`;

const Meta = styled.div`
  color: #4b5563;
  font-size: 15px;
  line-height: 1.5;
  margin-top: 8px;
`;

const Dates = styled.div`
  color: #6b7280;
  display: flex;
  flex-wrap: wrap;
  font-size: 14px;
  gap: 4px 16px;
  line-height: 1.5;
  margin-top: 10px;
`;

const RoomRefund = styled.div`
  color: #8a6d1a;
  font-size: 13px;
  font-weight: 600;
  margin-top: 10px;
`;

const SectionHeading = styled.h2`
  color: #6b7280;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.04em;
  margin: 4px 0 10px;
  text-transform: uppercase;
`;

const SubHeading = styled.div`
  color: #6b7280;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  margin: 12px 0 8px;
  text-transform: uppercase;
`;

const CardTitle = styled.div`
  color: #1a1a1a;
  font-size: 16px;
  font-weight: 700;
`;

const Muted = styled.div`
  color: #6b7280;
  font-size: 14px;
  line-height: 1.55;
  margin-top: 6px;
`;

const DepositCard = styled.div`
  align-items: center;
  background: #fff;
  border: 1px solid #ececec;
  border-radius: 14px;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  margin-bottom: 16px;
  margin-top: 6px;
  padding: 16px 18px;
`;

const RefundCard = styled.div`
  background: #fff;
  border: 1px solid #ececec;
  border-radius: 14px;
  margin-top: 6px;
  padding: 18px;
`;

const SectionRow = styled.div`
  align-items: center;
  color: #c9a961;
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
`;

const RefundLine = styled.div`
  color: #1a1a1a;
  font-size: 15px;
  margin-top: 6px;
`;

const PendingNote = styled.div`
  background: #fbf2dd;
  border-radius: 9px;
  color: #8a6d1a;
  font-size: 13px;
  margin-top: 12px;
  padding: 10px 12px;
`;

const Batches = styled.div`
  margin-top: 8px;
`;

const BatchRow = styled.div`
  align-items: center;
  border-top: 1px solid #f0f0f0;
  display: flex;
  font-size: 14px;
  gap: 12px;
  justify-content: space-between;
  padding: 10px 0;
`;

const BatchStatus = styled.span`
  align-items: center;
  color: ${(p) => (p.$done ? "#1f7a44" : "#8a6d1a")};
  display: inline-flex;
  font-size: 12px;
  font-weight: 700;
  gap: 3px;
  margin-left: 8px;
`;

const ClaimLink = styled.a`
  align-items: center;
  color: #c9a961;
  display: inline-flex;
  font-size: 13px;
  font-weight: 700;
  gap: 4px;
  text-decoration: none;
  white-space: nowrap;
`;

const Primary = styled.button`
  align-items: center;
  background: #c9a961;
  border: none;
  border-radius: 9px;
  color: #ffffff;
  cursor: pointer;
  display: flex;
  font-size: 14px;
  font-weight: 600;
  gap: 6px;
  justify-content: center;
  margin-top: 12px;
  padding: 11px 16px;

  &:disabled {
    opacity: 0.6;
  }

  .spin {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const ErrorBox = styled.div`
  background: #fdecec;
  border-radius: 8px;
  color: #c0392b;
  font-size: 13px;
  margin-bottom: 14px;
  padding: 10px 12px;
`;

export default BookingDetail;
